// Pure, dependency-free holdout assignment (grill §3.5-3.7). The holdout is a
// deterministic hash of phone+campaign, not a random draw: the same phone and
// campaign always land in the same bucket, so re-materialising a campaign
// reproduces the identical holdout set. Stratified by recency segment so a
// small audience can't come up short a holdout arm by chance (grill §3.6.1).
// Import-free so it is unit tested outside Deno and reused by
// src/utils/smsShared.ts.

export type RecencySegment = "active" | "cooling" | "at_risk" | "lapsing" | "lost" | "unknown";

// Mirrors v_customer_lifecycle's boundaries (RevenuePlays.tsx):
// active <30, cooling 30-60, at_risk 60-90, lapsing 90-180, lost >=180.
export function recencySegment(lastOrderAt: string | null, now: Date): RecencySegment {
  if (!lastOrderAt) return "unknown";
  const days = (now.getTime() - new Date(lastOrderAt).getTime()) / 86_400_000;
  if (days < 30) return "active";
  if (days < 60) return "cooling";
  if (days < 90) return "at_risk";
  if (days < 180) return "lapsing";
  return "lost";
}

// FNV-1a 32-bit — a fast, deterministic, pure-JS hash. Not cryptographic;
// only uniformity across phone+campaign pairs is required here.
function fnv1a(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/** A stable bucket in [0, 100) for this phone+campaign pair. */
export function holdoutBucket(customerPhone: string, campaignId: string): number {
  return fnv1a(`${customerPhone}|${campaignId}`) % 100;
}

/**
 * A stable bucket in [0, 100) for this phone+campaign+segment triple, used
 * only for the stratified holdout threshold test below. Segment-scoping the
 * hash (rather than ranking within the segment) means a member's own holdout
 * status depends only on their own phone+campaign+segment — never on which
 * other members happen to be in the audience — so it stays fixed even when
 * the eligible audience for the campaign shifts between calls.
 */
function segmentedHoldoutBucket(customerPhone: string, campaignId: string, segment: RecencySegment): number {
  return fnv1a(`${customerPhone}|${campaignId}|${segment}`) % 100;
}

export interface HoldoutAudienceMember {
  customer_phone: string;
  last_order_at: string | null;
}

export interface HoldoutAssignment {
  customer_phone: string;
  segment: RecencySegment;
  bucket: number;
  is_holdout: boolean;
}

/**
 * Stratified holdout assignment: within each recency segment, a member is
 * held out when their own segment-scoped hash falls under the `pct`
 * threshold (grill §3.6.1) — a fixed test on that member's own
 * phone+campaign+segment, not a rank within the current audience array,
 * so a member's holdout status is stable regardless of which other members
 * happen to be in the audience on a given call (a plain rank-based cut would
 * let one member's status flip purely because someone else's eligibility
 * changed, even though their own hash didn't). `pct <= 0` holds out none;
 * `pct >= 100` holds out everyone.
 */
export function assignHoldouts(
  audience: HoldoutAudienceMember[],
  campaignId: string,
  pct: number,
  now: Date = new Date(),
): HoldoutAssignment[] {
  return audience.map((member) => {
    const segment = recencySegment(member.last_order_at, now);
    const threshold = segmentedHoldoutBucket(member.customer_phone, campaignId, segment);
    return {
      customer_phone: member.customer_phone,
      segment,
      bucket: holdoutBucket(member.customer_phone, campaignId),
      is_holdout: pct > 0 && threshold < pct,
    };
  });
}
